import API_ROUTES from "../constants/api";
import HttpUtil from "../http-handler/http-util";
import { DEFAULT_ITEMS_PER_PAGE } from "../constants/common";

export default class CompanyService {
  static createCompany(params) {
    return HttpUtil.post(API_ROUTES.COMPANY.COMPANYCREATEAPI, params);
  }
  static listCompany(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort = ""
  ) {
    return HttpUtil.get(
      API_ROUTES.COMPANY.COMPANYLISTAPI.replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace("<key>", key)
        .replace("<sort>", sort)
    );
  }
  static getCompanySubscriptions(companyId, regulationType='') {
    return HttpUtil.get(
      API_ROUTES.COMPANY.COMPANYSUBSCRIPTIONSAPI.replace(
        "<companyId>",
        companyId
      ).replace(
        regulationType
          ? "<regulationType>"
          : "?regulationType=<regulationType>",
        regulationType
      )
    );
  }
  static getCompanyAssociations(companyId, pageNumber = "", pageSize = "") {
    return HttpUtil.get(
      API_ROUTES.COMPANY.COMPANYASSOCIATIONS.replace("<companyId>", companyId)
        .replace(
          pageNumber ? "<pageNumber>" : "?pageNumber=<pageNumber>",
          pageNumber
        )
        .replace(pageSize ? "<pageSize>" : "&pageSize=<pageSize>", pageSize)
    );
  }
  static getCompanyContextRegulations(encodedUrlParam) {
    return HttpUtil.get(
      API_ROUTES.COMPANY.COMPANYCONTEXTREGULATION.replace(
        "<encodedUrlParam>",
        encodedUrlParam
      )
    );
  }
  static getSuppliersCount() {
    return HttpUtil.get(API_ROUTES.COMPANY.SUPPLIERSCOUNT);
  }
  static getSuppliersCountByRegulation(companyId, regulationId, date) {
    return HttpUtil.get(
      API_ROUTES.COMPANY.SUPPLIERSCOUNTBYREGULATION.replace(
        "<companyId>",
        companyId
      )
        .replace("<regulationId>", regulationId)
        .replace("<date>", date)
    );
  }
  static getSupplierAccessibleRegulations() {
    return HttpUtil.get(API_ROUTES.COMPANY.SUPPLIER_ACCESSIBLE_REGULATIONS);
  }

  static getEnterpriseStructure() {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISE_STRUCTURE.replace("<showAll>", true));
  }

  static getEnterprise(id) {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISE.replace("<businessUnitId>", id));
  }

  static createEnterprise(payload) {
    return HttpUtil.post(API_ROUTES.COMPANY.ADD_ENTERPRISE, payload);
  }

  static getEnterpriseChildren(id) {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISE_CHILDREN.replace("<businessUnitId>", id));
  }

  static updateEnterprise(payload) {
    return HttpUtil.patch(API_ROUTES.COMPANY.UPDATE_ENTERPRISE, payload);
  }

  static deleteEnterprise(id) {
    return HttpUtil.delete(API_ROUTES.COMPANY.DELETE_ENTERPRISE.replace("<businessUnitId>", id));
  }

  static reorderEnterprise(id, payload) {
    return HttpUtil.patch(API_ROUTES.COMPANY.REORDER_ENTERPRISE.replace("<unitId>", id), payload);
  }

  static getEnterpriseTypes() {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISE_TYPES);
  }

  static getEnterprisesForType(type) {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISES_FOR_TYPE.replace("<buType>", type));
  }

  static getEnterprisesByLevel(level) {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISES_BY_LEVEL.replace("<level>", level));
  }

  static getIndustries() {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_INDUSTRIES);
  }

  static getMetricQuestions(
    standardId,
    period, 
    categoryType = 0,
    topicId = 0,
  ){
    return HttpUtil.get(
      API_ROUTES.COMPANY.GET_FILTERED_METRIC_QUESTIONS
      .replace("<standardId>", standardId)
      .replace("<categoryType>", categoryType)
      .replace("<period>", period)   
      .replace("<topicId>", topicId)
    )
  }
  
  static postMetricAnswers(period, payload) {
    return HttpUtil.post(
      API_ROUTES.COMPANY.POST_METRIC_ANS.replace("<period>", period),
      payload
    );
  }

  /**
   * Same path as postMetricAnswers; qualitative table search only — append excludePeriodic + topic scope.
   */
  static postMetricBulkAnswerForSearch(period, payload, queryContext = null) {
    let url = API_ROUTES.COMPANY.POST_METRIC_ANS.replace("<period>", period);
    if (queryContext != null && typeof queryContext === 'object') {
      const parts = ['excludePeriodic=true'];
      const topicIds = queryContext.topicIds;
      if (Array.isArray(topicIds) && topicIds.length === 1) {
        parts.push(`topicId=${encodeURIComponent(String(topicIds[0]))}`);
      } else if (Array.isArray(topicIds) && topicIds.length > 1) {
        parts.push(`topicIds=${encodeURIComponent(topicIds.join(','))}`);
      }
      url += `?${parts.join('&')}`;
    }
    return HttpUtil.post(url, payload);
  }

  static deleteMetricAnswer(answerId) {
    return HttpUtil.delete(API_ROUTES.COMPANY.DELETE_METRIC_ANS.replace("<answerId>", answerId));
  }
  
  static changeMetricQuestionStatus(questionId, status) {
    return HttpUtil.patch(
      API_ROUTES.COMPANY.UPDATE_METRIC_QN_STATUS
      .replace("<questionId>", questionId)
      .replace("<status>", status)
    );
  }

  static getLanguageList() {
    return HttpUtil.get(API_ROUTES.LANGUAGE.GET_LANGUAGES);
  }

  static getCompanyLanguageList() {
    return HttpUtil.get(API_ROUTES.LANGUAGE.GET_COMPANY_LANGUAGES);
  }

  static getMatIssuesCategory() {
    return HttpUtil.get(API_ROUTES.MATERIAL_ISSUES.GET_MATERIAL_ISSUES_CAT);
  }

  static getEnterpriseStructureMin() {
    return HttpUtil.get(API_ROUTES.COMPANY.GET_ENTERPRISE_STRUCTURE_MIN);
  }

  /**
   * Qualitative table search (metric answers) — GET .../standard/{id}/metric/{categoryType}/answer/{period}/search
   * Mirrors narratives GET_FILTERED_METRIC_QUESTIONS: same path segment as category (0/1/2) plus topic scope + excludePeriodic.
   */
  static searchQualitativeMetricAnswers(
    standardId,
    categoryType,
    period,
    search,
    pageSize = 10,
    pageNumber = 0,
    topicIds = null
  ) {
    const path = API_ROUTES.COMPANY.QUALITATIVE_METRIC_ANSWER_SEARCH.replace(
      "<standardId>",
      standardId
    )
      .replace("<metricId>", categoryType)
      .replace("<period>", encodeURIComponent(period));
    const parts = [
      `search=${encodeURIComponent(search ?? "")}`,
      `pageSize=${encodeURIComponent(String(pageSize))}`,
      `pageNumber=${encodeURIComponent(String(pageNumber))}`,
      "excludePeriodic=true",
    ];
    if (Array.isArray(topicIds) && topicIds.length === 1) {
      parts.push(`topicId=${encodeURIComponent(String(topicIds[0]))}`);
    } else if (Array.isArray(topicIds) && topicIds.length > 1) {
      parts.push(`topicIds=${encodeURIComponent(topicIds.join(","))}`);
    }
    return HttpUtil.get(`${path}?${parts.join("&")}`);
  }

  /**
   * Qualitative table search (disclosure answers) — GET .../disclosure/answer/{period}/search
   */
  static searchQualitativeDisclosureAnswers(
    period,
    search,
    pageSize = 10,
    pageNumber = 0
  ) {
    const path = API_ROUTES.COMPANY.QUALITATIVE_DISCLOSURE_ANSWER_SEARCH.replace(
      "<period>",
      encodeURIComponent(period)
    );
    const qs = new URLSearchParams({
      search: search ?? "",
      pageSize: String(pageSize),
      pageNumber: String(pageNumber),
    }).toString();
    return HttpUtil.get(`${path}?${qs}`);
  }

  static downloadBusinessUnitTemplate() {
    return HttpUtil.getExcelData(API_ROUTES.COMPANY.DOWNLOAD_BUSINESS_UNIT_TEMPLATE);
  }

  static importBusinessUnitData(file) {
    const formData = new FormData();
    formData.append('file', file);
    return HttpUtil.post(API_ROUTES.COMPANY.IMPORT_BUSINESS_UNIT_DATA, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static deleteMetricAnswers(answerIds) {
    return HttpUtil.delete(API_ROUTES.COMPANY.DELETE_METRIC_ANS_BULK, { data: answerIds });
  }
}
